import {
	Card,
	CardContent,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
	Grid,
	Switch,
} from "@mui/material";
import Feature from "../../../common/components/Feature";
import CustomSelect from "../../../common/components/CustomSelect";
import CustomInput from "../../../common/components/CustomInput";
import { PricingRequestData } from "../pricingRequestStepper.module";
import { PayoutMethod } from "../PricingRequestStepper.constant";
import { FeatureName } from "../../../common/hooks/useFeatureFlags";
import { ReactNode } from "react";
import { If } from "../../../common/components/If";

export interface CapFloorCardProps {
	values: PricingRequestData;
	handleChange: any;
	setFieldValue: (fieldName: string, value: any) => void;
	errors: any;
	gridSize: number;
	wrap: boolean;
	showCapFloor: boolean;
	showCapFloorIntraday: boolean;
}

export interface CardWrapperProps {
	wrap: boolean;
	children: ReactNode;
}

function CardWrapper(props: CardWrapperProps) {
	if (!props.wrap) {
		return <>{props.children}</>;
	}
	return (
		<Card sx={{ minWidth: "100%" }}>
			<CardContent>{props.children}</CardContent>
		</Card>
	);
}

export default function CapFloorCard(props: CapFloorCardProps) {
	const {
		values,
		handleChange,
		setFieldValue,
		errors,
		gridSize,
		wrap,
		showCapFloor,
		showCapFloorIntraday,
	} = props;

	return (
		<Feature name={FeatureName.pricing_stepper_cap_floor} is_active={true}>
			<Grid
				item
				xs={gridSize}
				display="flex"
				flexDirection={"column"}
				justifyContent="flex-start"
			>
				<If condition={showCapFloor}>
					<CardWrapper wrap={wrap}>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										name={"hasPayoutMethod"}
										checked={values.hasPayoutMethod}
										disabled={values.hasFixPrice}
										onChange={(e) => {
											setFieldValue(
												"hasPayoutMethod",
												e.target.checked
											);
											setFieldValue(
												"payoutMethod",
												undefined
											);
											setFieldValue("cap", "");
											setFieldValue("floor", "");
										}}
									/>
								}
								label="Cap/Floor"
							/>
						</FormGroup>
						<CustomSelect
							datatestId="payoutMethod"
							name="payoutMethod"
							label="Method"
							value={values.payoutMethod ?? ""}
							disabled={!values.hasPayoutMethod}
							onChange={handleChange}
							error={!!errors.payoutMethod}
							errorText={errors.payoutMethod}
							touched={true}
							items={Object.values(PayoutMethod).map((val) => ({
								key: val,
								value: val,
								label: val,
							}))}
						/>
						<FormControl>
							<FormLabel>Floor</FormLabel>
							<CustomInput
								name="floor"
								placeholder="Floor"
								disabled={
									!values.hasPayoutMethod ||
									values.payoutMethod === "CAP"
								}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.floor}
								error={!!errors.floor}
								errorText={errors.floor}
								touched={true}
							/>
						</FormControl>
						{wrap && <br />}
						<FormControl>
							<FormLabel>Cap</FormLabel>
							<CustomInput
								name="cap"
								placeholder="Cap"
								disabled={
									!values.hasPayoutMethod ||
									values.payoutMethod === "FLOOR"
								}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.cap}
								error={!!errors.cap}
								errorText={errors.cap}
								touched={true}
							/>
						</FormControl>
					</CardWrapper>
				</If>
			</Grid>
			<Grid
				item
				xs={gridSize}
				display="flex"
				flexDirection={"column"}
				justifyContent="flex-start"
			>
				<CardWrapper wrap={wrap}>
					<If condition={showCapFloorIntraday}>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										name={"hasPayoutMethodIntraDay"}
										checked={values.hasPayoutMethodIntraDay}
										disabled={values.hasFixPrice}
										onChange={(e) => {
											setFieldValue(
												"hasPayoutMethodIntraDay",
												e.target.checked
											);
											setFieldValue(
												"payoutMethodIntraDay",
												undefined
											);
											setFieldValue("capIntraDay", "");
											setFieldValue("floorIntraDay", "");
										}}
									/>
								}
								label="Cap/Floor intraday"
							/>
						</FormGroup>
						<CustomSelect
							datatestId="payoutMethodIntraDay"
							name="payoutMethodIntraDay"
							label="Method"
							value={values.payoutMethodIntraDay ?? ""}
							disabled={!values.hasPayoutMethodIntraDay}
							onChange={handleChange}
							error={!!errors.payoutMethodIntraDay}
							errorText={errors.payoutMethodIntraDay}
							touched={true}
							items={Object.values(PayoutMethod).map((val) => ({
								key: val,
								value: val,
								label: val,
							}))}
						/>
						<FormControl>
							<FormLabel>Floor</FormLabel>
							<CustomInput
								name="floorIntraDay"
								placeholder="Floor"
								disabled={
									!values.hasPayoutMethodIntraDay ||
									values.payoutMethodIntraDay === "CAP"
								}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.floorIntraDay}
								error={!!errors.floorIntraDay}
								errorText={errors.floorIntraDay}
								touched={true}
							/>
						</FormControl>
						{wrap && <br />}
						<FormControl>
							<FormLabel>Cap</FormLabel>
							<CustomInput
								name="capIntraDay"
								placeholder="Cap"
								disabled={
									!values.hasPayoutMethodIntraDay ||
									values.payoutMethodIntraDay === "FLOOR"
								}
								onChange={handleChange}
								variant="standard"
								type="number"
								value={values.capIntraDay}
								error={!!errors.capIntraDay}
								errorText={errors.capIntraDay}
								touched={true}
							/>
						</FormControl>
					</If>
				</CardWrapper>
			</Grid>
		</Feature>
	);
}
